import styled from "@emotion/styled";
import { Formik } from "formik";
import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import {
    Button,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Alert as MuiAlert,
    TextField as MuiTextField,
    OutlinedInput,
} from "@mui/material";
import { spacing } from "@mui/system";

import { VisibilityOffOutlined, VisibilityOffRounded } from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";
import useTheme from "../../hooks/useTheme";
import { apiGatewayAxiosInstance, BUTTON_BACKGROUND_COLOR, BUTTON_TEXT_COLOR } from "../../utils/axios";
import { config, EUserPasswordStatus } from "../../config";
import { is } from "date-fns/locale";
import FetchingSpinner from "../../pages/components/FetchingSpinner";

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

function SignIn() {
    const { signIn } = useAuth();
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = React.useState(false);
    const [loading, setLoading] = useState(false);
    const [errorResponse, setErrorResponse] = useState("");
    const { theme } = useTheme();
    const passwordTextFieldRef = useRef(null);
    const signInButtonRef = useRef(null);
    const [limitError, setLimitError] = useState("");

    const handleClickShowPassword = () => setShowPassword(show => !show);

    const handleMouseDownPassword = event => {
        event.preventDefault();
    };

    const sendOtp = async userData => {
        // setLoading(true);
        const response = await apiGatewayAxiosInstance.post("user_management/mfa/initiate-mfa", userData);
        // setLoading(false);
        return response;
    };

    return (
        <>
            {" "}
            {loading ? (
                <FetchingSpinner />
            ) : (
                <Formik
                    initialValues={{
                        userName: "",
                        password: "",
                        submit: false,
                    }}
                    validationSchema={Yup.object().shape({
                        userName: Yup.string().max(50).required("Username is required"),
                        password: Yup.string().max(50).required("Password is required"),
                    })}
                    onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                        try {
                            const { response, isLoggedIn } = await signIn(values.userName, values.password);
                            if (isLoggedIn) {
                                const user = response.data.data;

                                const payload = {
                                    userId: user._id,
                                    registryId: user.roleId,
                                    orgId: user.convertedOrgId ?? "",
                                    roleName: user.roleName,
                                };
                                if (user.status === EUserPasswordStatus.PASSWORD_NOT_RESET) {//for login for the first time
                                    sessionStorage.setItem("accessToken", response.data.accessToken);
                                    navigate(`/auth/first-login/${user._id}`);
                                } else {
                                    const token = sessionStorage.getItem("accessToken");
                                    if (!response.data.needVerifyMfa && token) {// to check if user has verified mfa and if yes then redirect to dashboard
                                        navigate("/dashboard");
                                        return;
                                    }
                                    // const otpResponse = await sendOtp(payload);
                                    // if (otpResponse) {
                                    navigate(`/auth/validate`, { state: payload });
                                    // } else {
                                    //     navigate("/auth/login");
                                    // }
                                }
                            }
                        } catch (error) {


                            let message = error.message || "Something went wrong";
                            if (error.response && error.response.data && error.response.data.message) {
                                message = error.response.data.message;
                            }

                            setStatus({ success: false });
                            setErrors({ submit: message });
                            // setLimitError(message);
                            setSubmitting(false);
                            setLoading(false);
                        }
                    }}
                >
                    {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                        <form noValidate onSubmit={handleSubmit}>
                            {errors.submit && (
                                <Alert mt={2} mb={3} severity="warning">
                                    {errors.submit}
                                </Alert>
                            )}
                            <TextField
                                name="userName"
                                label="Username"
                                value={values.userName}
                                autocomplete="new-password"
                                // focused

                                inputProps={{
                                    autocomplete: "new-password",
                                    form: {
                                        autocomplete: "off",
                                    },
                                    maxLength: 50,
                                }}
                                error={Boolean(touched.userName && errors.userName)}
                                fullWidth
                                helperText={touched.userName && errors.userName}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                my={2}
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        e.preventDefault(); // prevent form submit
                                        if (passwordTextFieldRef.current) {
                                            passwordTextFieldRef.current.focus(); // shift focus to Verify button
                                        }
                                    }
                                }}
                            />

                            <FormControl fullWidth variant="outlined" className="mt-3" error={Boolean(touched.password && errors.password)}>
                                <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
                                <OutlinedInput
                                    inputRef={passwordTextFieldRef}
                                    id="outlined-adornment-password"
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    label="Password"
                                    value={values.password}
                                    error={Boolean(touched.password && errors.password)}
                                    fullWidth
                                    inputProps={{
                                        autocomplete: "new-password",
                                        form: {
                                            autocomplete: "off",
                                        },
                                        maxLength: 50,
                                    }}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOffOutlined /> : <VisibilityOffRounded />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    // helperText={touched.password && errors.password}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    my={2}
                                    onKeyDown={e => {
                                        if (e.key === "Enter") {
                                            e.preventDefault(); // prevent form submit
                                            if (signInButtonRef.current) {
                                                signInButtonRef.current.focus(); // shift focus to Verify button
                                            }
                                        }
                                    }}
                                />
                                <FormHelperText>{touched.password && errors.password}</FormHelperText>
                            </FormControl>

                            <Button
                                ref={signInButtonRef}
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{
                                    backgroundColor: BUTTON_BACKGROUND_COLOR,
                                    color: BUTTON_TEXT_COLOR,
                                    "&:hover": {
                                        backgroundColor: BUTTON_BACKGROUND_COLOR, // Keep the background color the same on hover
                                        boxShadow: "none", // Optionally, remove any hover shadow effect
                                    },
                                }}
                                // sx={{ backgroundColor: "primary" }}
                                // color='primary'
                                className="mt-3"
                                disabled={isSubmitting}
                            >
                                Sign in
                            </Button>
                            {/* <RCSButton >Sign In</RCSButton> */}
                            <Button component={Link} to="/auth/reset-password" fullWidth className="mt-3">
                                Forgot password
                            </Button>
                            {/* <p>We have just sent an email with a password resent link to email@gmail.com</p> */}

                            {limitError && (
                                <Alert mt={2} mb={3} severity="warning">
                                    {limitError}
                                </Alert>
                            )}
                        </form>
                    )}
                </Formik >
            )
            }
            <div
                style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "10px",
                    fontSize: "13px",
                }}
            >
                Version : {config.REACT_APP_BUILD_NUMBER}
            </div>
        </>
    );
}

export default SignIn;
